import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import PremiumInput from "../ui/PremiumInput";
import PremiumSelect from "../ui/PremiumSelect";
import PremiumCheckbox from "../ui/PremiumCheckbox";
import { DartsEvent } from "../../types";
import { eventsApi } from "../../api/events";
import type { EventCreateRequest, EventGroup } from "../../api/events";
import { UI_TEXT } from "../../constants/uiText";

interface EventFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (body: EventCreateRequest) => Promise<boolean>;
  isEdit?: boolean;
  event?: DartsEvent | null;
}

interface EventGroupOption {
  label: string;
  value: string;
}

function displayDateToInput(display: string): string {
  // "2026 / 03 / 15 13:00" → "2026-03-15T13:00"
  const parts = display.split(" / ");
  if (parts.length < 3) return "";
  const [year, month, rest] = parts;
  const [day, time] = rest.split(" ");
  return `${year}-${month}-${day}T${time}`;
}

const EventFormModal: React.FC<EventFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isEdit = false,
  event,
}) => {
  const [tournament, setTournament] = useState("");
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [location, setLocation] = useState("");
  const [isSingles, setIsSingles] = useState(true);
  const [matchFormat, setMatchFormat] = useState("");
  const [fee, setFee] = useState("");
  const [other, setOther] = useState("");
  const [hasPoints, setHasPoints] = useState(true);
  const [isExciting, setIsExciting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [eventGroups, setEventGroups] = useState<EventGroupOption[]>([]);
  const [isLoadingEventGroups, setIsLoadingEventGroups] = useState(false);
  const [eventGroupsError, setEventGroupsError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const fetchEventGroups = async () => {
      setIsLoadingEventGroups(true);
      try {
        const data = await eventsApi.getEventGroups();
        if (cancelled) return;
        setEventGroups(toEventGroupOptions(data));
        setEventGroupsError(null);
      } catch {
        if (cancelled) return;
        setEventGroups([]);
        setEventGroupsError(UI_TEXT.eventForm.loadEventGroupsError);
      } finally {
        if (!cancelled) setIsLoadingEventGroups(false);
      }
    };

    fetchEventGroups();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    if (event) {
      setTitle(event.title || event.tournament || "");
      setDate(displayDateToInput(event.date));
      setLocation(event.location ?? "");
      setIsSingles(event.singlesFlag ?? true);
      setMatchFormat(event.matchFormat ?? "");
      setFee(event.fee ?? "");
      setOther(event.notes ?? "");
      setHasPoints(event.hasPoints ?? true);
      setIsExciting(event.isExciting ?? false);
    } else {
      setTitle("");
      setDate("");
      setLocation("");
      setIsSingles(true);
      setMatchFormat("");
      setFee("");
      setOther("");
      setHasPoints(true);
      setIsExciting(false);
    }
  }, [event, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    if (event) {
      if (event.eventGroupId) {
        const matchedById = eventGroups.find(
          (option) => option.value === event.eventGroupId,
        );
        if (matchedById) {
          setTournament(matchedById.value);
          return;
        }
      }

      const matchedByName = eventGroups.find(
        (option) => option.label === event.tournament,
      );
      if (matchedByName) {
        setTournament(matchedByName.value);
        return;
      }
    }

    setTournament((current) => {
      if (current && eventGroups.some((option) => option.value === current)) {
        return current;
      }
      return eventGroups[0]?.value ?? "";
    });
  }, [event, eventGroups, isOpen]);

  const handleSubmit = async () => {
    if (isSubmitting || !tournament) return;

    // ローカル時刻（東京時間）をISO 8601形式に変換
    const formatDateTimeForAPI = (dateTimeStr: string): string => {
      if (!dateTimeStr) return "";
      // Input: "2026-03-15T13:00" → Output: ISO 8601 形式
      const [datePart, timePart] = dateTimeStr.split("T");
      const [year, month, day] = datePart.split("-");
      const [hours, minutes] = timePart.split(":");

      // 東京時間（UTC+9）として解釈してUTCに変換
      const date = new Date(
        `${year}-${month}-${day}T${hours}:${minutes}:00+09:00`,
      );
      return date.toISOString();
    };

    const body: EventCreateRequest = {
      event_groups_id: tournament,
      title,
      datetime: formatDateTimeForAPI(date),
      place: location,
      singles_flag: isSingles,
      singles_point_flag: isSingles && hasPoints,
      singles_ex_point_flag: isSingles && isExciting,
      doubles_flag: !isSingles,
      doubles_point_flag: !isSingles && hasPoints,
      match_format: matchFormat,
      participation_fee: fee,
      other,
    };

    setIsSubmitting(true);
    try {
      const ok = await onSubmit(body);
      if (ok) onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectOptions =
    eventGroups.length > 0
      ? eventGroups
      : [
          {
            label: isLoadingEventGroups
              ? UI_TEXT.common.loading
              : UI_TEXT.eventForm.noEventGroups,
            value: "",
          },
        ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="bg-white w-full max-w-sm rounded-[40px] overflow-hidden shadow-2xl animate-in zoom-in-95 flex flex-col max-h-[85vh]">
        <div className="bg-slate-900 p-8 text-white flex justify-between items-center shrink-0 text-left">
          <div>
            <h3 className="text-2xl font-black tracking-tighter uppercase leading-none">
              {isEdit
                ? UI_TEXT.eventForm.titleEdit
                : UI_TEXT.eventForm.titleCreate}
            </h3>
            <p className="text-[9px] text-slate-400 font-bold tracking-[0.2em] uppercase mt-2">
              DARTS LIFE
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 active:scale-90 transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-8 space-y-2 overflow-y-auto scrollbar-hide text-left">
          <PremiumSelect
            label={UI_TEXT.eventForm.labels.tournament}
            options={selectOptions}
            value={tournament}
            onChange={(e) => setTournament(e.target.value)}
            disabled={isLoadingEventGroups || eventGroups.length === 0}
          />
          {eventGroupsError && (
            <p className="mb-4 text-xs font-medium text-rose-500">
              {eventGroupsError}
            </p>
          )}
          <PremiumInput
            label={UI_TEXT.eventForm.labels.eventName}
            placeholder={UI_TEXT.eventForm.placeholders.eventName}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <PremiumInput
            label={UI_TEXT.eventForm.labels.date}
            type="datetime-local"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <PremiumInput
            label={UI_TEXT.eventForm.labels.location}
            placeholder="Heroes Cafe"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
          <div className="mb-4 relative group text-left">
            <label className="block text-[10px] font-bold text-slate-500 mb-1.5 uppercase pl-1 tracking-widest">
              {UI_TEXT.eventForm.labels.matchFormat}
            </label>
            <textarea
              placeholder={UI_TEXT.eventForm.placeholders.matchFormat}
              className="w-full bg-slate-50 border border-slate-200 p-3 rounded-xl text-sm font-bold outline-none resize-y min-h-[96px]"
              value={matchFormat}
              onChange={(e) => setMatchFormat(e.target.value)}
            />
          </div>
          <div className="space-y-4">
            <PremiumCheckbox
              label={UI_TEXT.eventForm.labels.singles}
              checked={isSingles}
              onChange={setIsSingles}
            />
            <div className="text-left">
              <label className="block text-[10px] font-bold text-slate-500 mb-1.5 uppercase pl-1">
                {UI_TEXT.eventForm.labels.fee}
              </label>
              <textarea
                placeholder="¥2,000"
                className="w-full bg-slate-50 border border-slate-200 p-3 rounded-xl text-sm font-bold outline-none h-[46px] resize-y"
                value={fee}
                onChange={(e) => setFee(e.target.value)}
              />
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <PremiumCheckbox
              label={UI_TEXT.eventForm.labels.hasPoints}
              checked={hasPoints}
              onChange={setHasPoints}
            />
            <PremiumCheckbox
              label={UI_TEXT.eventForm.labels.exciting}
              checked={isExciting}
              onChange={setIsExciting}
            />
          </div>
          <div className="mb-4 relative group text-left">
            <label className="block text-[10px] font-bold text-slate-500 mb-1.5 uppercase pl-1 tracking-widest">
              {UI_TEXT.eventForm.labels.notes}
            </label>
            <textarea
              placeholder={UI_TEXT.eventForm.placeholders.notes}
              className="w-full bg-slate-50 border border-slate-200 p-3 rounded-xl text-sm font-bold outline-none resize-y min-h-[96px]"
              value={other}
              onChange={(e) => setOther(e.target.value)}
            />
          </div>
          <button
            className="w-full bg-slate-900 text-white py-5 rounded-2xl font-black uppercase tracking-widest shadow-xl active:scale-95 transition-all mt-4"
            onClick={handleSubmit}
            disabled={
              isSubmitting || isLoadingEventGroups || eventGroups.length === 0
            }
          >
            {UI_TEXT.eventForm.submit}
          </button>
        </div>
      </div>
    </div>
  );
};

function toEventGroupOptions(groups: EventGroup[]): EventGroupOption[] {
  return groups.map((group) => ({
    label: group.name,
    value: group.id,
  }));
}

export default EventFormModal;
